import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiClient } from '../services/api';
import { RoadMapPage } from './RoadMapPage';
import { PageHeader } from './PageHeader';
import type { UserNFT } from '../types';
import './WhiteListPage.css';

interface WhiteListStatus {
  in_white_list: boolean;
  nft_id?: number;
  joined_at?: string;
}

interface WhiteListStats {
  total_spots: number;
  spots_taken: number;
  remaining_spots: number;
  white_list_start_date: string;
  opendoor_start_date?: string | null;
  is_late_user?: boolean;
}


export const WhiteListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromSbpLinking = (location.state as { fromSbpLinking?: boolean })?.fromSbpLinking ?? false;
  const [status, setStatus] = useState<WhiteListStatus | null>(null);
  const [stats, setStats] = useState<WhiteListStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [isLateUser, setIsLateUser] = useState(false);
  const [nftStats, setNftStats] = useState<{ total: number; available: number; max_step_price?: number } | null>(null);
  const [firstNft, setFirstNft] = useState<UserNFT | null>(null);

  // Fetch white list data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statusData, statsData] = await Promise.all([
          apiClient.getWhiteListStatus(),
          apiClient.getWhiteListStats(),
        ]);
        setStatus(statusData);
        setStats(statsData);
        setIsLateUser(statsData.is_late_user || false);

        // Fetch NFT stats separately so it doesn't block the page on error
        try {
          const nftStatsData = await apiClient.getNftStats();
          setNftStats(nftStatsData);
        } catch {
          console.error('Failed to fetch NFT stats');
        }

        // Fetch user's NFTs for status card
        try {
          const myNftsData = await apiClient.getMyNfts();
          if (statusData.in_white_list && myNftsData.nfts.length > 0) {
            setFirstNft(myNftsData.nfts[0]);
          }
        } catch {
          console.error('Failed to fetch user NFTs');
        }
      } catch (error) {
        console.error('Failed to fetch white list data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleViewNFT = useCallback(() => {
    navigate('/my-nfts');
  }, [navigate]);

  if (loading) {
    return (
      <div className="whitelist-page">
        <PageHeader title="White List" showBack={!fromSbpLinking} />
        <div className="whitelist-loading">
          <div className="whitelist-spinner"></div>
        </div>
      </div>
    );
  }

  // Show RoadMapPage for users who joined after White List start
  if (isLateUser) {
    return (
      <RoadMapPage
        stats={stats}
        whiteListStatus={status}
        maxUserStepPrice={nftStats?.max_step_price ?? 0}
        showBack={!fromSbpLinking}
      />
    );
  }

  return (
    <div className="whitelist-page">
      <PageHeader title="White List" showBack={!fromSbpLinking} />
      {/* NFT Progress Bar */}
      {nftStats && (
        <div className="whitelist-nft-progress-section">
          <div className="progress-top-row">
            <span className="progress-top-label">До старта продаж:</span>
            <span className="progress-top-value">
              {nftStats.available} <span className="progress-top-total">/ {nftStats.total} мест</span>
            </span>
          </div>
          <div className="progress-bar-container">
            <div className="progress-bar-horizontal">
              <div
                className="progress-bar-fill"
                style={{ width: `${nftStats.total > 0 ? ((nftStats.total - nftStats.available) / nftStats.total) * 100 : 0}%` }}
              />
            </div>
          </div>
          <span className="progress-whitelist-note">
            Только для участников White list
          </span>
        </div>
      )}

      {/* Combined Status + Info Block */}
      <div className="whitelist-card combined-card">
        {/* Status section */}
        {status?.in_white_list && (
          <div className="status-card-inner">
            <div className="status-card-row">
              {firstNft && (
                <img
                  src={firstNft.image_url}
                  alt={firstNft.title}
                  className="status-card-nft-image"
                />
              )}
              <div className="status-card-text">
                <span className="status-card-title">Вы уже в White List!</span>
                <span className="status-card-subtitle">
                  Ваш NFT получен и ждёт вас в&nbsp;разделе &laquo;Мои ху#ки&raquo;
                </span>
              </div>
            </div>
            <button className="status-card-btn button--accent button--accent-s" onClick={handleViewNFT}>
              Смотреть &laquo;Мои ху#ки&raquo;
            </button>
          </div>
        )}

        {/* Info section */}
        <h2 className="card-title">Что такое White List?</h2>
        <p className="card-text">
          Это ранний доступ для первых 1000 участников, которые:
        </p>
        <div className="info-benefits-block">
          <div className="benefit-highlight">
            <span className="benefit-highlight-text">Получат свой первый NFT</span>
            <span className="benefit-highlight-badge">бесплатно!</span>
          </div>
          <div className="benefits-icons-list">
            <div className="benefit-icon-row">
              <div className="benefit-icon-wrap">
                <img src="/file-text.png" alt="" className="benefit-icon-img" />
              </div>
              <span className="benefit-icon-text">Попадут в участие на всех этапах ху#ков</span>
            </div>
            <div className="benefit-icon-row">
              <div className="benefit-icon-wrap">
                <img src="/crown.png" alt="" className="benefit-icon-img" />
              </div>
              <span className="benefit-icon-text">Смогут покупать NFT раньше всех остальных</span>
            </div>
          </div>
        </div>
      </div>

      {/* Prize Tiers Block */}
      <div className="whitelist-card prizes-card">
        <h3 className="card-subtitle">Ху#вые призы</h3>
        <p className="card-text">
          В дропе участвуют 4 тира NFT — от базового до премиального. Чем выше тир, тем ценнее и реже призы
        </p>
        <img src="/prizetiers.png" alt="Тиры призов" className="prizes-image" />
        <button className="prizes-learn-more" onClick={() => navigate('/faq')}>
          Узнать больше <span className="prizes-learn-more-arrow">&rsaquo;</span>
        </button>
      </div>
    </div>
  );
};
